import {
	getViemClients,
} from "@bracket/contractor-next";

import {
	getContractWithABI,
	getTokenDefinition,
	type ValidChainId,
} from "@bracket/chainography";

import type { TokenIdentifier, } from "@bracket/types";
import type { Config } from "wagmi";



/**
 * @description Get the balance of a token
 * @param {TokenIdentifier} token - The token to get the balance of
 * @param {string} owner - The owner of the token
 * @param {string} target - The target address to get the balance of
 * @param {boolean} bigint - Whether to return the balance in bigint form
 * @param {ValidChainId} chainId - The chain id to use
 * @param {Config} config - The config to use
 */
export declare type BalanceOfProps = {
	token?: TokenIdentifier;
	owner?: string;
	target?: string;
	bigint?: boolean;
	chainId?: ValidChainId;
	config?: Config;
	block?: number | "latest";
};



/**
 * @description Get the balance of a token
 * @param {TokenIdentifier} token - The token to get the balance of
 * @param {string?} owner - An alternative contract address to retrieve the balance
 * @param {string?} target - An alternative wallet address being queried for a balance
 * @param {number|"latest"?} block - The block number representing the time of the balance
 * @param {ValidChainId?} chainId - The chain id to use, defaults to 1
 * @param {Config?} config - The wagmi/viem config to use - always send if you're running in a browser
 * @returns {Promise<bigint>} The balance of the token
 */
export const balanceOf = async ({
	token,
	owner,
	target,
	block = "latest",
	chainId = 1,
	config,
}: BalanceOfProps ): Promise<bigint> => {
	try {

		const [client, wallet] = await getViemClients({ config, chainId, });
		const {address} = owner ? { address:owner } : getTokenDefinition(token);
		const [_, abi] = getContractWithABI("ERC20");
		const blockNumber = block === "latest" ? undefined : BigInt(block);


		const result = await client.readContract({
			address,
			abi,
			functionName: "balanceOf",
			args: [
				target || wallet.account.address,
			],
			blockNumber,
		})

		if(typeof result === "bigint") {
			return result;
		}

		throw new Error("unexpected result - expected bigint");

	} catch (err) {

		throw new Error(`@bracket/chainography/erc20:balanceOf ${err.message}`);
	}

};
